import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useSubscription } from "@/context/SubscriptionContext";
import { BottomNavigation } from "@/components/layout/BottomNavigation";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ShopFooter from "@/components/ShopFooter";
import axios from "axios";
import { toast } from "sonner";
import { 
  Smile, 
  Moon, 
  Heart,
  PenLine,
  X,
  ChevronLeft,
  ArrowLeft,
  Send,
  BookOpen,
  Sparkles,
  Zap,
  RefreshCw,
  Plus,
  ShoppingBag,
  Bell,
  Compass,
  Lock,
  ArrowRight
} from "lucide-react";
import { hasFeatureAccess, MEMBERSHIP_URL } from "@/lib/subscriptionAccess";

const TRUENORTH_STORE_URL = "https://www.truenorth-zone.com/collections";

// Source filter tabs - removed "All"
const SOURCE_FILTERS = [
  { id: "journal", label: "Journal", icon: PenLine, source: null },
  { id: "gratitude", label: "Gratitude", icon: Heart, source: "gratitude_prompt" },
  { id: "mood", label: "Mood", icon: Smile, source: "mood_tracker" },
  { id: "energy", label: "Energy", icon: Zap, source: "energy_check" },
  { id: "reframe", label: "Reframe", icon: RefreshCw, source: "reframe_tool" },
];

const REFLECTION_CARDS = [
  {
    id: "feeling",
    title: "How Are You Feeling?",
    subtitle: "Check-In",
    prompt: "How are you feeling right now?",
    icon: Smile,
    gradient: "from-[#d0e0e8] via-[#c8d8e8] to-[#d8d0e8]",
    iconColor: "text-[#5a7090]"
  },
  {
    id: "sleep",
    title: "How Did You Sleep?",
    subtitle: "Check-In", 
    prompt: "How was your sleep last night? Any dreams or thoughts upon waking?",
    icon: Moon,
    gradient: "from-[#d8d0e8] via-[#e0d8e8] to-[#e8d8e0]",
    iconColor: "text-[#7a6090]"
  },
  {
    id: "grateful",
    title: "What Are You Grateful For?",
    subtitle: "Check-In",
    prompt: "What are you grateful for today?",
    icon: Heart,
    gradient: "from-[#f0d8d8] via-[#e8d0d0] to-[#f0e0d8]",
    iconColor: "text-[#a07070]"
  },
  {
    id: "reflect",
    title: "Take a Moment to Reflect",
    subtitle: "Journal",
    prompt: "What's on your mind? Take a moment to reflect on your day, your thoughts, or anything you want to process.",
    icon: PenLine,
    gradient: "from-[#e0e0e0] via-[#e8e8e8] to-[#d8d8d8]",
    iconColor: "text-[#707070]"
  }
];

const MOOD_OPTIONS = [
  { id: "calm", label: "Calm", emoji: "😌", color: "bg-green-500/20 text-green-400 border-green-500/30" },
  { id: "anxious", label: "Anxious", emoji: "😰", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  { id: "happy", label: "Happy", emoji: "😊", color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
  { id: "sad", label: "Sad", emoji: "😢", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  { id: "tired", label: "Tired", emoji: "😴", color: "bg-purple-500/20 text-purple-400 border-purple-500/30" },
  { id: "energized", label: "Energized", emoji: "⚡", color: "bg-orange-500/20 text-orange-400 border-orange-500/30" },
  { id: "reflective", label: "Reflective", emoji: "🤔", color: "bg-indigo-500/20 text-indigo-400 border-indigo-500/30" },
  { id: "hopeful", label: "Hopeful", emoji: "🌟", color: "bg-pink-500/20 text-pink-400 border-pink-500/30" },
];

const SLEEP_OPTIONS = [
  { id: "great", label: "Great", emoji: "😴", description: "Slept well, feeling rested" },
  { id: "good", label: "Good", emoji: "🙂", description: "Decent sleep" },
  { id: "okay", label: "Okay", emoji: "😐", description: "Could be better" },
  { id: "poor", label: "Poor", emoji: "😫", description: "Struggled to sleep" },
  { id: "terrible", label: "Terrible", emoji: "😵", description: "Barely slept" },
];

export default function ReflectionsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const { subscription } = useSubscription();
  const [activeReflection, setActiveReflection] = useState(null);
  const [selectedMood, setSelectedMood] = useState(null);
  const [selectedSleep, setSelectedSleep] = useState(null);
  const [reflectionText, setReflectionText] = useState("");
  const [saving, setSaving] = useState(false);
  const [recentEntries, setRecentEntries] = useState([]);
  const [sourceFilter, setSourceFilter] = useState("journal"); // Default to Journal
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addType, setAddType] = useState(null); // 'journal', 'gratitude', 'mood', 'energy', 'reframe'
  const [newEntryText, setNewEntryText] = useState("");
  const [savingNewEntry, setSavingNewEntry] = useState(false);
  
  // Check PathFinder access using subscription context
  const hasPathfinderAccess = hasFeatureAccess(subscription.type, 'pathfinder', user);

  // Read tab from URL query param on mount
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && SOURCE_FILTERS.some(f => f.id === tabParam)) {
      setSourceFilter(tabParam);
    }
  }, [searchParams]);

  const fetchRecentEntries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/journal?limit=50`, {
        withCredentials: true
      });
      setRecentEntries(res.data || []);
    } catch (error) {
      console.error("Error fetching entries:", error);
    }
  }, []);

  useEffect(() => {
    fetchRecentEntries();
  }, [fetchRecentEntries]);

  // Filter entries by source
  // Journal tab shows ALL entries (aggregation from all other tabs)
  // Other tabs show only entries from that specific source
  const filteredEntries = recentEntries.filter(entry => {
    if (sourceFilter === "journal") {
      // Journal tab shows all entries from Gratitude, Mood, Energy, and Reframe
      return entry.source === "gratitude_prompt" || 
             entry.source === "mood_tracker" || 
             entry.source === "energy_check" || 
             entry.source === "reframe_tool";
    }
    if (sourceFilter === "gratitude") return entry.source === "gratitude_prompt";
    if (sourceFilter === "mood") return entry.source === "mood_tracker";
    if (sourceFilter === "energy") return entry.source === "energy_check";
    if (sourceFilter === "reframe") return entry.source === "reframe_tool";
    return true;
  });

  // Handle Add New button click
  const handleAddNew = () => {
    setAddType(sourceFilter);
    setNewEntryText("");
    setShowAddDialog(true);
  };

  // Save new entry from Add dialog
  const handleSaveNewEntry = async () => {
    if (!newEntryText.trim() || newEntryText.trim().length < 5) {
      toast.error("Please add at least 5 characters to your entry");
      return;
    }

    // Get the source mapping for the current tab
    const sourceMap = {
      gratitude: "gratitude_prompt",
      mood: "mood_tracker", 
      energy: "energy_check",
      reframe: "reframe_tool"
    };

    const source = sourceMap[addType] || "gratitude_prompt";
    const labelMap = {
      gratitude: "Gratitude",
      mood: "Mood",
      energy: "Energy",
      reframe: "Reframe"
    };

    setSavingNewEntry(true);
    try {
      await axios.post(
        `${API}/journal`,
        { 
          title: `${labelMap[addType] || "Gratitude"} Entry`,
          body: newEntryText.trim(),
          tags: [addType, "reflection"],
          source: source
        },
        { withCredentials: true }
      );
      toast.success("Entry saved!");
      setShowAddDialog(false);
      setNewEntryText("");
      fetchRecentEntries();
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save entry");
    } finally {
      setSavingNewEntry(false);
    }
  };

  const handleCardClick = (card) => {
    setActiveReflection(card);
    setSelectedMood(null);
    setSelectedSleep(null);
    setReflectionText("");
  };

  const handleClose = () => {
    setActiveReflection(null);
    setSelectedMood(null);
    setSelectedSleep(null);
    setReflectionText("");
  };

  const handleSave = async () => {
    if (!activeReflection) return;
    
    let title = activeReflection.title;
    let body = "";
    let tags = ["reflection", activeReflection.id];
    
    if (activeReflection.id === "feeling" && selectedMood) {
      body = `Mood: ${selectedMood.label} ${selectedMood.emoji}\n\n${reflectionText}`.trim();
      tags.push(`mood-${selectedMood.id}`);
    } else if (activeReflection.id === "sleep" && selectedSleep) {
      body = `Sleep Quality: ${selectedSleep.label} ${selectedSleep.emoji}\n\n${reflectionText}`.trim();
      tags.push(`sleep-${selectedSleep.id}`);
    } else if (activeReflection.id === "grateful") {
      body = `Gratitude:\n${reflectionText}`.trim();
      tags.push("gratitude");
    } else {
      body = reflectionText.trim();
    }
    
    if (!body || body.length < 5) {
      toast.error("Please add some content to your reflection");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(
        `${API}/journal`,
        { title, body, tags },
        { withCredentials: true }
      );
      toast.success("Reflection saved!");
      handleClose();
      fetchRecentEntries();
    } catch (error) {
      toast.error("Failed to save reflection");
    } finally {
      setSaving(false);
    }
  };

  const canSave = () => {
    if (activeReflection?.id === "feeling") return selectedMood !== null;
    if (activeReflection?.id === "sleep") return selectedSleep !== null;
    return reflectionText.trim().length >= 5;
  };

  return (
    <div className="min-h-screen bg-background pb-28">
      {/* Header */}
      <header 
        className="sticky top-0 z-40 backdrop-blur-lg border-b border-black/5"
        style={{
          backgroundImage: `linear-gradient(rgba(11,40,33,0.85), rgba(11,40,33,0.9)), url('https://customer-assets.emergentagent.com/job_path-unfolds/artifacts/va4i7lvj_ChatGPT%20Image%20Feb%2014%2C%202026%2C%2012_49_01%20PM.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")} className="p-2 -ml-2">
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div>
              <h1 className="text-lg font-heading text-[#a69373]">Reflections</h1>
              <p className="text-xs text-[#a69373]/60 font-semibold">Your journal entries</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="w-9 h-9 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
              <Bell className="w-4 h-4 text-[#a69373]" />
            </button>
            <a 
              href="https://www.truenorth-zone.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/30 backdrop-blur-sm text-[#a69373] text-sm font-semibold hover:bg-black/40 transition-colors"
              style={{ zIndex: 9999, position: 'relative', pointerEvents: 'all' }}
            >
              <ShoppingBag className="w-4 h-4" />
              Shop
            </a>
          </div>
        </div>
        
        {/* Source Filter Tabs */}
        <div className="px-4 pt-4 pb-3 overflow-x-auto scrollbar-hide" style={{ position: 'relative', zIndex: 30 }}>
          <div className="flex gap-2 min-w-max">
            {SOURCE_FILTERS.map((filter) => {
              const isActive = sourceFilter === filter.id;
              return (
                <button
                  key={filter.id}
                  onClick={() => setSourceFilter(filter.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all duration-200 ${
                    isActive
                      ? "bg-primary"
                      : "bg-card text-foreground/70 hover:bg-card/80"
                  }`}
                  style={{ 
                    ...(isActive ? { color: '#ffffff', fontWeight: 700 } : { fontWeight: 700 }),
                    pointerEvents: 'auto',
                    position: 'relative',
                    zIndex: 31
                  }}
                  data-testid={`filter-${filter.id}`}
                >
                  <filter.icon 
                    className="w-3.5 h-3.5" 
                    style={isActive ? { color: '#ffffff', stroke: '#ffffff' } : {}}
                  />
                  {filter.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="px-4 py-6">
        {/* PathFinder AI Coach Hero Card */}
        <Card 
          className="mb-6 overflow-hidden border-0 cursor-pointer transition-transform hover:scale-[1.01]"
          style={{
            background: 'linear-gradient(135deg, #1a3a2f 0%, #0b2821 50%, #1a2a35 100%)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(166,147,115,0.15)'
          }}
          onClick={() => {
            if (hasPathfinderAccess) {
              navigate('/pathfinder');
            } else {
              window.open(MEMBERSHIP_URL, '_blank');
            }
          }}
          data-testid="pathfinder-hero-card"
        >
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              {/* Icon */}
              <div 
                className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
                style={{
                  background: 'linear-gradient(135deg, #a69373 0%, #8a7a5f 100%)',
                  boxShadow: '0 4px 12px rgba(166,147,115,0.3)'
                }}
              >
                <Compass className="w-7 h-7 text-[#0b2821]" />
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-[#a69373] text-lg font-heading">PathFinder</h3>
                  <span 
                    className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                    style={{
                      background: 'rgba(166,147,115,0.15)',
                      color: '#a69373'
                    }}
                  >
                    PATHFINDER
                  </span>
                </div>
                <p className="text-white/70 text-sm mb-3">
                  Your AI companion for reflection. Notice patterns and think things through.
                </p>
                
                {/* CTA Button */}
                {hasPathfinderAccess ? (
                  <div className="flex items-center gap-2 text-[#a69373] text-sm font-semibold">
                    <Sparkles className="w-4 h-4" />
                    Open PathFinder
                    <ArrowRight className="w-4 h-4" />
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-[#a69373]/70 text-sm font-semibold">
                    <Lock className="w-4 h-4" />
                    Upgrade to Unlock
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Add New Button - hidden on Journal tab since it's an aggregation view */}
        {sourceFilter !== "journal" && (
          <Button 
            className="w-full btn-primary mb-6"
            onClick={handleAddNew}
            data-testid="add-new-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New {SOURCE_FILTERS.find(f => f.id === sourceFilter)?.label}
          </Button>
        )}

        {/* Journal tab description */}
        {sourceFilter === "journal" && (
          <div className="text-center mb-6 p-4 rounded-lg bg-white/5">
            <p className="text-foreground/60 text-sm">
              A collection of all your reflections from Gratitude, Mood, Energy, and Reframe entries.
            </p>
          </div>
        )}

        {/* Entries List */}
        {filteredEntries.length > 0 ? (
          <section>
            <h2 className="text-lg font-heading text-[#a69373] mb-4">
              {sourceFilter === "journal" ? "All Reflections" : `${SOURCE_FILTERS.find(f => f.id === sourceFilter)?.label} Entries`}
            </h2>
            <div className="space-y-3">
              {filteredEntries.slice(0, 20).map((entry) => (
                <Card 
                  key={entry.entry_id} 
                  className="glass-card overflow-hidden cursor-pointer"
                  onClick={() => navigate("/journal")}
                >
                  <CardContent className="p-4">
                    <div className="flex gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#d4e4d4] flex items-center justify-center shrink-0">
                        <PenLine className="w-5 h-5 text-[#5a8a5a]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-foreground/60 text-xs">
                            {new Date(entry.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <p className="text-foreground text-sm line-clamp-2">{entry.body}</p>
                        {entry.tags?.length > 0 && (
                          <div className="flex gap-1 mt-2 flex-wrap">
                            {entry.tags.slice(0, 3).map((tag) => (
                              <span 
                                key={tag} 
                                className="px-2 py-0.5 rounded-full bg-[#e0ebe0] text-xs"
                                style={{ color: '#2d4a44', fontWeight: 700 }}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        ) : (
          <section>
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-card flex items-center justify-center">
                {SOURCE_FILTERS.find(f => f.id === sourceFilter)?.icon && (
                  <>{React.createElement(SOURCE_FILTERS.find(f => f.id === sourceFilter).icon, { className: "w-8 h-8 text-foreground/30" })}</>
                )}
              </div>
              {sourceFilter === "journal" ? (
                <>
                  <p className="text-foreground/50 text-sm mb-4">
                    No reflections yet. Start by adding entries in Gratitude, Mood, Energy, or Reframe tabs.
                  </p>
                  <Button 
                    className="btn-primary"
                    onClick={() => setSourceFilter("gratitude")}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Start with Gratitude
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-foreground/50 text-sm mb-4">
                    No {SOURCE_FILTERS.find(f => f.id === sourceFilter)?.label.toLowerCase()} entries yet.
                  </p>
                  <Button 
                    className="btn-primary"
                    onClick={handleAddNew}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First {SOURCE_FILTERS.find(f => f.id === sourceFilter)?.label}
                  </Button>
                </>
              )}
            </div>
          </section>
        )}
        
        {/* Shop Footer */}
        <ShopFooter />
      </main>

      {/* Reflection Modal */}
      <Dialog open={activeReflection !== null} onOpenChange={() => handleClose()}>
        <DialogContent className="sm:max-w-md bg-[#f5f9f5] border-[#c5d5c5] p-0 gap-0 max-h-[90vh] overflow-y-auto">
          {activeReflection && (
            <>
              {/* Header with gradient */}
              <div className={`h-24 relative bg-gradient-to-br ${activeReflection.gradient}`}>
                <button 
                  onClick={handleClose}
                  className="absolute top-4 left-4 w-8 h-8 rounded-full bg-black/20 flex items-center justify-center"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
                <div className="absolute bottom-4 left-4 right-4">
                  <h2 className="text-white font-heading text-xl">{activeReflection.title}</h2>
                </div>
              </div>
              
              <div className="p-5 space-y-5">
                <p className="text-foreground/70 text-sm">{activeReflection.prompt}</p>
                
                {/* Mood Selection */}
                {activeReflection.id === "feeling" && (
                  <div className="grid grid-cols-4 gap-2">
                    {MOOD_OPTIONS.map((mood) => (
                      <button
                        key={mood.id}
                        onClick={() => setSelectedMood(mood)}
                        className={`p-3 rounded-xl border transition-all duration-200 ${
                          selectedMood?.id === mood.id
                            ? mood.color + " border-2"
                            : "bg-[#e0ebe0] border-[#c5d5c5] hover:bg-[#d4e4d4]"
                        }`}
                        data-testid={`mood-${mood.id}`}
                      >
                        <span className="text-2xl block mb-1">{mood.emoji}</span>
                        <span className="text-xs text-foreground/70">{mood.label}</span>
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Sleep Selection */}
                {activeReflection.id === "sleep" && (
                  <div className="space-y-2">
                    {SLEEP_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => setSelectedSleep(option)}
                        className={`w-full p-3 rounded-xl border flex items-center gap-3 transition-all duration-200 ${
                          selectedSleep?.id === option.id
                            ? "bg-[#d4e4d4] border-[#7a9e7a]"
                            : "bg-[#e0ebe0] border-[#c5d5c5] hover:bg-[#d4e4d4]"
                        }`}
                        data-testid={`sleep-${option.id}`}
                      >
                        <span className="text-2xl">{option.emoji}</span>
                        <div className="text-left">
                          <span className="text-foreground font-medium block">{option.label}</span>
                          <span className="text-foreground/50 text-xs">{option.description}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Text Input */}
                <Textarea
                  placeholder={
                    activeReflection.id === "feeling" 
                      ? "Add more details about how you're feeling (optional)..."
                      : activeReflection.id === "sleep"
                      ? "Any dreams or thoughts upon waking? (optional)"
                      : activeReflection.id === "grateful"
                      ? "What are you grateful for today?"
                      : "Write your thoughts here..."
                  }
                  value={reflectionText}
                  onChange={(e) => setReflectionText(e.target.value)}
                  className="min-h-[120px] bg-white border-[#c5d5c5] text-foreground placeholder:text-foreground/40 resize-none"
                  data-testid="reflection-textarea"
                />
                
                <Button
                  className="w-full btn-primary"
                  onClick={handleSave}
                  disabled={!canSave() || saving}
                  data-testid="save-reflection"
                >
                  {saving ? "Saving..." : "Save Reflection"}
                  <Send className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add New Entry Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => {
        setShowAddDialog(open);
        if (!open) {
          setNewEntryText("");
        }
      }}>
        <DialogContent className="sm:max-w-md bg-[#f5f9f5] border-[#c5d5c5] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1a1a1a] flex items-center gap-2">
              {addType && SOURCE_FILTERS.find(f => f.id === addType)?.icon && 
                React.createElement(SOURCE_FILTERS.find(f => f.id === addType).icon, { className: "w-5 h-5 text-[#7b9e7b]" })
              }
              New {SOURCE_FILTERS.find(f => f.id === addType)?.label || "Entry"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Prompt text based on type */}
            <p className="text-sm text-[#1a1a1a]/70">
              {addType === "gratitude" && "What are you grateful for today?"}
              {addType === "mood" && "How are you feeling right now? What's on your mind?"}
              {addType === "energy" && "How is your energy level? What's affecting it?"}
              {addType === "reframe" && "What negative thought would you like to reframe into something positive?"}
            </p>
            
            <Textarea
              value={newEntryText}
              onChange={(e) => setNewEntryText(e.target.value)}
              placeholder={`Write your ${SOURCE_FILTERS.find(f => f.id === addType)?.label.toLowerCase() || "entry"} here...`}
              className="min-h-[150px] bg-white border-[#c5d5c5] text-[#1a1a1a] resize-none"
              data-testid="new-entry-textarea"
            />
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-[#c5d5c5] text-[#1a1a1a]"
                onClick={() => setShowAddDialog(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 btn-primary"
                onClick={handleSaveNewEntry}
                disabled={!newEntryText.trim() || newEntryText.trim().length < 5 || savingNewEntry}
                data-testid="save-new-entry"
              >
                {savingNewEntry ? "Saving..." : "Save Entry"}
                <Send className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <BottomNavigation />
    </div>
  );
}
